import { API, graphqlOperation } from 'aws-amplify';
import { getR0003Reportv2 } from '../../../../../graphql/queries';
import { fetchMetricDataByClientDateAndName } from '../../../../../services/metricData/fetchMetricDataByClientDateAndName';
import { fetchTrainingHoursForRange } from '../../../../../services/trainingHoursService';
import { format, startOfWeek, endOfWeek, parseISO, addDays } from 'date-fns';

const METRIC_NAMES = {
  FOH_REGULAR_HOURS: 'fohRegularHours',
  FOH_OVERTIME_HOURS: 'fohOvertimeHours',
  BOH_REGULAR_HOURS: 'bohRegularHours',
  BOH_OVERTIME_HOURS: 'bohOvertimeHours',
  ACT_FOH: 'actFOH',
  ACT_BOH: 'actBOH',
};

/**
 * Fetches and transforms labor-hours data for R0006.
 *
 * @param {string} startDate - Range start ('yyyy-MM-dd', inclusive)
 * @param {string} endDate - Range end ('yyyy-MM-dd', inclusive)
 * @param {Object} user - Authenticated user (userName, client used for queries)
 * @param {string} role - User role (used for R0003 query)
 * @param {string[]} [stores] - Optional store-name filter
 * @param {Map|null} [storeIdMap] - Optional storeName -> storeRecordId map for MetricData lookups
 * @param {Object|null} [preLoadedR0003Data] - Optional pre-fetched R0003 data to avoid a duplicate query
 * @param {Object|null} [trainingData] - Optional pre-fetched training-hours map. Shape:
 *   `{ [weekStart]: { [storeName]: { [dayName]: { foh: number, boh: number } } } }`
 *   When omitted, the function fetches one week at a time for every ISO week
 *   that overlaps [startDate, endDate] via `fetchTrainingHoursForRange`.
 * @returns {Promise<Array>} Per-store labor-hours rows for the table.
 */
export const fetchLaborHoursData = async (startDate, endDate, user, role, stores = [], storeIdMap = null, preLoadedR0003Data = null, trainingData = null) => {
  console.log('🕒 FETCHING LABOR HOURS DATA');
  console.log('🔧 Labor Hours Parameters:', {
    user: user.userName || user.displayName,
    role: role,
    stores: stores,
    storeCount: stores.length,
    hasPreLoadedR0003: !!preLoadedR0003Data
  });

  try {
    let r0003Data;
    
    // Use pre-loaded R0003 data if available, otherwise fetch it
    if (preLoadedR0003Data && preLoadedR0003Data.length > 0) {
      // Convert from sales service format to the expected format
      r0003Data = {
        data: preLoadedR0003Data
      };
    } else {
      
      // Get the week string for R0003 query (format: YYYY-MM-DD)
      const weekStart = format(startOfWeek(parseISO(startDate), { weekStartsOn: 0 }), 'yyyy-MM-dd');
      
      // Fetch R0003 report data for scheduled and theoretical hours
      const r0003Response = await API.graphql(
        graphqlOperation(getR0003Reportv2, {
          week: weekStart,
          user: user.userName,
          role: role,
        })
      );
      
      r0003Data = r0003Response.data.getR0003Reportv2;
      
      if (!r0003Data) {
        console.error('🚨 R0003 QUERY RETURNED NULL/UNDEFINED');
        throw new Error(`No R0003 data found for the selected date range`);
      }
    }
    
    if (!r0003Data.data || r0003Data.data.length === 0) {
      console.error('🚨 R0003 DATA IS EMPTY');
      console.error('🚨 R0003 Response Details:', {
        hasData: !!r0003Data,
        dataLength: r0003Data?.data?.length || 0,
        fullResponse: r0003Data
      });
      throw new Error(`R0003 data exists but contains no areas for the selected date range`);
    }
    
    console.log('✅ R0003 DATA RECEIVED:', {
      areaCount: r0003Data.data.length,
      areas: r0003Data.data.map(area => area.area),
      sampleAreaData: r0003Data.data[0]
    });
    
    // Fetch actual hours from MetricData
    let metricData;
    try {
      metricData = await fetchMetricDataByClientDateAndName(
        user.client,
        startDate,
        endDate,
        METRIC_NAMES
      );
    } catch (metricError) {
      console.error('🚨 MetricData fetch failed:', metricError);
      throw new Error(`Failed to fetch labor hours metric data: ${metricError?.message || 'Unknown error'}`);
    }
    
    if (!metricData || !Array.isArray(metricData) || metricData.length === 0) {
      console.error('🚨 NO METRIC DATA RECEIVED OR INVALID FORMAT');
      console.error('🚨 MetricData Details:', {
        hasMetricData: !!metricData,
        isArray: Array.isArray(metricData),
        length: metricData?.length || 0,
        sampleData: metricData?.slice(0, 3) || []
      });
      throw new Error('No labor hours metric data available for the selected date range');
    }
    
    console.log('✅ METRIC DATA RECEIVED:', {
      recordCount: metricData.length,
      metrics: [...new Set(metricData.map(m => m.metricName))],
      stores: [...new Set(metricData.map(m => m.storeId))].slice(0, 5),
      dateRange: {
        earliest: Math.min(...metricData.map(m => m.date)),
        latest: Math.max(...metricData.map(m => m.date))
      }
    });

    // Fetch training hours for every ISO week that overlaps the range.
    // The result is keyed by weekStart so the transform can look up the
    // correct week's training when iterating multi-week R0003 data.
    let resolvedTrainingData = trainingData;
    if (!resolvedTrainingData) {
      try {
        resolvedTrainingData = await fetchTrainingHoursForRange({
          user,
          startDate,
          endDate
        });
        console.log('✅ TRAINING DATA RECEIVED:', {
          weekCount: Object.keys(resolvedTrainingData || {}).length,
          weeks: Object.keys(resolvedTrainingData || {})
        });
      } catch (trainingError) {
        // Training data fetch failure is non-fatal - continue with empty training data
        console.warn('⚠️ Training data fetch failed (continuing without):', trainingError?.message);
        resolvedTrainingData = {};
      }
    }

    // Transform the data
    try {
      console.log('🔄 TRANSFORMING LABOR HOURS DATA:', {
        r0003DataCount: r0003Data?.data?.length || 0,
        metricDataCount: metricData?.length || 0,
        storesFilter: stores,
        hasStoreIdMap: !!storeIdMap,
        hasTrainingData: Object.keys(resolvedTrainingData || {}).length > 0
      });

      const transformedData = transformLaborHoursData(r0003Data, metricData, stores, storeIdMap, startDate, endDate, resolvedTrainingData);
      
      console.log('✅ LABOR HOURS TRANSFORM COMPLETE:', {
        resultCount: transformedData?.length || 0,
        sampleStores: transformedData?.slice(0, 3)?.map(d => d.area) || []
      });
      
      return transformedData;
    } catch (transformError) {
      console.error('🚨 ERROR IN TRANSFORM:', transformError);
      throw new Error(`Failed to transform labor hours data: ${transformError?.message || 'Unknown transform error'}`);
    }
  } catch (error) {
    console.error('🚨 LABOR HOURS DATA ERROR:', error);
    console.error('🚨 Error details:', {
      message: error?.message,
      name: error?.name,
      stack: error?.stack?.split('\n').slice(0, 3)
    });
    
    // Ensure we always throw an error with a message
    const errorMessage = error?.message || error?.toString() || 'Unknown labor hours data error';
    throw new Error(errorMessage);
  }
};

const transformLaborHoursData = (r0003Data, metricData, filterStores = [], storeIdMap = null, startDate = null, endDate = null, trainingData = null) => {
  if (!r0003Data || !r0003Data.data) {
    console.error('🚨 Invalid R0003 data in transform');
    return [];
  }
  
  if (!metricData || !Array.isArray(metricData)) {
    console.error('🚨 Invalid metricData in transform - not an array:', typeof metricData);
    return [];
  }
  
  // Group metric data by store and date, summing up all dayParts
  const metricsByStoreDate = {};
  metricData.forEach(metric => {
    if (!metric) {
      console.warn('⚠️ Null metric record found');
      return;
    }
    
    const key = `${metric.storeId}_${metric.date}`;
    if (!metricsByStoreDate[key]) {
      metricsByStoreDate[key] = {};
    }
    
    // Sum up values for metrics with the same name (handles multiple dayParts like lunch/dinner)
    if (!metricsByStoreDate[key][metric.metricName]) {
      metricsByStoreDate[key][metric.metricName] = 0;
    }
    metricsByStoreDate[key][metric.metricName] += metric.numberValue || 0;
  });
  
  const results = [];
  
  // Global store aggregation tracker to prevent duplicates across areas
  const globalStoreAggregations = new Map();
  
  console.log('🔍 TRANSFORM FILTER LOGIC:', {
    filterStoresProvided: filterStores.length > 0,
    filterStores: filterStores,
    totalAreasInR0003: r0003Data.data.length
  });

  // Process each area in R0003 data to extract store-level data
  r0003Data.data.forEach(areaData => {
    const areaName = areaData.area;
    
    console.log(`🔍 Processing area: ${areaName}`);
    
    // NOTE: We don't filter at the area level anymore since we need to process 
    // stores within areas. The filtering will happen at the store level below.
    console.log(`✅ Processing all stores in area: ${areaName}`);
    
    // Handle the weekDataMap structure from sales service
    if (areaData.weekDataMap && areaData.weekDataMap.size > 0) {
      // Note: Using global storeAggregations to prevent duplicates across areas
      
      // Iterate through each week in the weekDataMap
      for (const [weekStart, weekData] of areaData.weekDataMap) {
        
        // Create date mapping for this week: Mon = weekStart, Tue = weekStart+1, etc.
        const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const weekStartDate = parseISO(weekStart);
        const dayToDateMap = {};
        DAYS_OF_WEEK.forEach((day, index) => {
          const dayDate = addDays(weekStartDate, index);
          dayToDateMap[day] = format(dayDate, 'yyyy-MM-dd');
        });
        
        // Parse the selected date range for filtering
        const selectedStartDate = parseISO(startDate);
        const selectedEndDate = parseISO(endDate);
        
        // R0003 structure: store at index 0, 11, 22, etc. Days follow each store (Mon=+1, Tue=+2, etc.)
        const STORE_BLOCK_SIZE = 11; // Each store takes 11 rows (store name + 7 days + 3 other rows)
        
        // Process each store block
        for (let storeIndex = 0; storeIndex < weekData.length; storeIndex += STORE_BLOCK_SIZE) {
          const storeNameData = weekData[storeIndex];
          
          // Stop if we hit "Area Rollup" or no more stores
          if (!storeNameData?.day || storeNameData.day.startsWith('Area Rollup')) {
            break;
          }
          
          const storeName = storeNameData.day;
          
          // Apply store-level filtering if provided
          if (filterStores.length > 0 && !filterStores.includes(storeName)) {
            console.log(`❌ Store ${storeName} filtered out (not in accessible stores)`);
            continue; // Skip this store
          }
          
          console.log(`✅ Store ${storeName} passed filter check`);

          // Initialize store aggregation if not exists
          if (!globalStoreAggregations.has(storeName)) {
            globalStoreAggregations.set(storeName, {
              area: storeName, // Store name for consistency with existing table structure
              areaName: areaName, // Keep track of which area this store belongs to
              foh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0, trainingHours: 0 },
              boh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0, trainingHours: 0 }
            });
          }
          
          const storeData = globalStoreAggregations.get(storeName);
          
          // Get store ID for metrics lookup
          const storeId = storeIdMap && storeIdMap.has(storeName) ? 
            storeIdMap.get(storeName) : storeName;
          
          // Process the 7 days following the store name (indices +1 through +7)
          DAYS_OF_WEEK.forEach((dayName, dayOffset) => {
            const dayIndex = storeIndex + dayOffset + 1; // +1 because days start after store name
            if (dayIndex >= weekData.length) return;
            
            const dayData = weekData[dayIndex];
            if (!dayData) return;
            
            // Get the actual date for this day of the week
            const date = dayToDateMap[dayName];
            if (!date) return;
            
            // Filter: only process dates within our selected range
            const dayDate = parseISO(date);
            if (dayDate < selectedStartDate || dayDate > selectedEndDate) {
              return;
            }
            
            // Find matching metric data for this store/date
            const metricsKey = `${storeId}_${date}`;
            const dayMetrics = metricsByStoreDate[metricsKey] || {};
            
            // Calculate actual FOH/BOH hours
            // Use pre-calculated actFOH/actBOH if available, otherwise sum the components
            const actualFOH = dayMetrics[METRIC_NAMES.ACT_FOH] ||
                             ((dayMetrics[METRIC_NAMES.FOH_REGULAR_HOURS] || 0) +
                              (dayMetrics[METRIC_NAMES.FOH_OVERTIME_HOURS] || 0));
            const actualBOH = dayMetrics[METRIC_NAMES.ACT_BOH] ||
                             ((dayMetrics[METRIC_NAMES.BOH_REGULAR_HOURS] || 0) +
                              (dayMetrics[METRIC_NAMES.BOH_OVERTIME_HOURS] || 0));

            // Get training hours for this store/day.
            // trainingData structure: { [weekStart]: { [storeName]: { [dayName]: { foh, boh } } } }
            // dayName here is abbreviated (Mon, Tue, etc.) but training uses full names (Monday, Tuesday, etc.)
            const ABBREV_TO_FULL = { 'Mon': 'Monday', 'Tue': 'Tuesday', 'Wed': 'Wednesday', 'Thu': 'Thursday', 'Fri': 'Friday', 'Sat': 'Saturday', 'Sun': 'Sunday' };
            const fullDayName = ABBREV_TO_FULL[dayName] || dayName;
            // trainingData is keyed by weekStart, then store, then full day name.
            // Looking up via weekStart ensures each ISO week in the range gets
            // its own week's training values (rather than reusing week 1's).
            const storeTraining = trainingData?.[weekStart]?.[storeName]?.[fullDayName] || { foh: 0, boh: 0 };
            const trainingFOH = storeTraining.foh || 0;
            const trainingBOH = storeTraining.boh || 0;

            // FOH calculations
            // Sched vs Model: (Sched - Training) - Theo
            const fohSchedVsModel = ((dayData.schedFOH || 0) - trainingFOH) - (dayData.theoFOH || 0);
            const fohChangeDuringDay = actualFOH - (dayData.schedFOH || 0);
            // Variance to Adjusted: Actual - AdjTheo
            // Training is deliberately NOT deducted here. The deduction shipped in
            // Apr 2026 and was reverted at the customer's request; training is
            // excluded from Sched vs Model above but not from this column.
            // See agent-os/specs/2026-08-21-r0006-revert-variance-training.
            // Consequence: the row does not cross-foot - SchedVsModel +
            // ChangeDuringDay - AllowedSPLH falls short of this value by the
            // training hours. That gap is expected, not a bug.
            const fohVarianceToAdjusted = actualFOH - (dayData.adjTheoFOH || 0);
            // Allowed SPLH: AdjTheo - Theo (simplified from composite formula)
            const fohAllowedSPLH = (dayData.adjTheoFOH || 0) - (dayData.theoFOH || 0);

            // BOH calculations
            // Sched vs Model: (Sched - Training) - Theo
            const bohSchedVsModel = ((dayData.schedBOH || 0) - trainingBOH) - (dayData.theoBOH || 0);
            const bohChangeDuringDay = actualBOH - (dayData.schedBOH || 0);
            // Variance to Adjusted: Actual - AdjTheo (training not deducted - see FOH note above)
            const bohVarianceToAdjusted = actualBOH - (dayData.adjTheoBOH || 0);
            // Allowed SPLH: AdjTheo - Theo (simplified from composite formula)
            const bohAllowedSPLH = (dayData.adjTheoBOH || 0) - (dayData.theoBOH || 0);

            // Add to store aggregation
            storeData.foh.schedVsModel += fohSchedVsModel;
            storeData.foh.changeDuringDay += fohChangeDuringDay;
            storeData.foh.allowedSPLH += fohAllowedSPLH;
            storeData.foh.varianceToAdjusted += fohVarianceToAdjusted;

            storeData.boh.schedVsModel += bohSchedVsModel;
            storeData.boh.changeDuringDay += bohChangeDuringDay;
            storeData.boh.allowedSPLH += bohAllowedSPLH;
            storeData.boh.varianceToAdjusted += bohVarianceToAdjusted;
            
            // Also track raw hour values for tooltips (accumulating across days)
            if (!storeData.foh.schedHours) storeData.foh.schedHours = 0;
            if (!storeData.foh.theoHours) storeData.foh.theoHours = 0;
            if (!storeData.foh.actualHours) storeData.foh.actualHours = 0;
            if (!storeData.foh.adjHours) storeData.foh.adjHours = 0;
            
            if (!storeData.boh.schedHours) storeData.boh.schedHours = 0;
            if (!storeData.boh.theoHours) storeData.boh.theoHours = 0;
            if (!storeData.boh.actualHours) storeData.boh.actualHours = 0;
            if (!storeData.boh.adjHours) storeData.boh.adjHours = 0;
            
            storeData.foh.schedHours += (Number(dayData.schedFOH) || 0);
            storeData.foh.theoHours += (Number(dayData.theoFOH) || 0);
            storeData.foh.actualHours += actualFOH;
            storeData.foh.adjHours += (Number(dayData.adjTheoFOH) || 0);
            storeData.foh.trainingHours += trainingFOH;

            storeData.boh.schedHours += (Number(dayData.schedBOH) || 0);
            storeData.boh.theoHours += (Number(dayData.theoBOH) || 0);
            storeData.boh.actualHours += actualBOH;
            storeData.boh.adjHours += (Number(dayData.adjTheoBOH) || 0);
            storeData.boh.trainingHours += trainingBOH;
          });
        }
        
        // Note: Stores will be added to results after processing all areas to prevent duplicates
        console.log(`📊 Processed area ${areaName}, global stores now: ${globalStoreAggregations.size}`);
      }
    } else {
      console.warn(`⚠️ Area ${areaName} has no weekDataMap or it's empty`);
    }
  });
  
  // Add all unique stores to results (deduplication happens automatically with Map)
  console.log('📦 Adding deduplicated stores to final results');
  for (const [storeName, storeData] of globalStoreAggregations) {
    console.log(`  ✅ Final result: ${storeName}`);
    results.push(storeData);
  }
  
  console.log('🎯 TRANSFORM SUMMARY:', {
    totalResultStores: results.length,
    storeNames: results.map(r => r.area),
    filterStoresApplied: filterStores.length > 0,
    filterStoresList: filterStores
  });
  
  // Sort results alphabetically by store name
  results.sort((a, b) => a.area.localeCompare(b.area));
  
  return results;
};

export const calculateLaborHoursTotals = (laborHoursData) => {
  if (!laborHoursData || !Array.isArray(laborHoursData)) {
    console.error('🚨 Invalid laborHoursData passed to calculateLaborHoursTotals:', laborHoursData);
    return {
      foh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
      boh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
    };
  }

  try {
    return laborHoursData.reduce((totals, store) => {
      if (!store?.foh || !store?.boh) {
        console.warn('⚠️ Invalid store data in calculateLaborHoursTotals:', store);
        return totals;
      }

      return {
        foh: {
          schedVsModel: totals.foh.schedVsModel + (store.foh.schedVsModel || 0),
          changeDuringDay: totals.foh.changeDuringDay + (store.foh.changeDuringDay || 0),
          allowedSPLH: totals.foh.allowedSPLH + (store.foh.allowedSPLH || 0),
          varianceToAdjusted: totals.foh.varianceToAdjusted + (store.foh.varianceToAdjusted || 0),
        },
        boh: {
          schedVsModel: totals.boh.schedVsModel + (store.boh.schedVsModel || 0),
          changeDuringDay: totals.boh.changeDuringDay + (store.boh.changeDuringDay || 0),
          allowedSPLH: totals.boh.allowedSPLH + (store.boh.allowedSPLH || 0),
          varianceToAdjusted: totals.boh.varianceToAdjusted + (store.boh.varianceToAdjusted || 0),
        },
      };
    }, {
      foh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
      boh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
    });
  } catch (error) {
    console.error('🚨 ERROR in calculateLaborHoursTotals:', error);
    return {
      foh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
      boh: { schedVsModel: 0, changeDuringDay: 0, allowedSPLH: 0, varianceToAdjusted: 0 },
    };
  }
};